import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Editor, EditorContent } from '@tiptap/react';
import { dispatchTextBlockData, useTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { Button } from '@dexma/ui-components';
import i18n from 'asset/translation';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { UpdateTextBlockCriteriaBuilder } from 'use-case/widget/update/updateTextBlockCriteria';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';
import { isSome } from 'fp-ts/lib/Option';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';

type TiptapProps = {
    editor: Editor | null;
    textBlockId: string;
    content: string;
    dashboardId?: string;
};

const TiptapWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;

    h1 {
        font-size: 36px;
    }

    h2 {
        font-size: 28px;
    }

    h3 {
        font-size: 22px;
    }

    h4 {
        font-size: 18px;
    }

    h5 {
        font-size: 16px;
    }

    img {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
        margin: 4px 0;
    }

    [data-resize-handle] {
        width: 12px;
        height: 12px;
    }

    [data-resize-handle='top-left'],
    [data-resize-handle='bottom-right'] {
        cursor: nwse-resize;
    }

    [data-resize-handle='top-right'],
    [data-resize-handle='bottom-left'] {
        cursor: nesw-resize;
    }
`;

const EditorContentWrapper = styled.div<{ $isDashboardFrame: boolean }>`
    flex: 1;
    min-height: 0;
    display: flex;
    padding: ${({ $isDashboardFrame }) => ($isDashboardFrame ? '0px 8px' : '8px')};

    .ProseMirror {
        height: 100%;
        outline: none;
    }
`;

const StyledEditorContent = styled(EditorContent)<{ $isBeingEdited: boolean }>`
    flex: 1;
    min-height: 0;
    height: 100%;

    ${({ $isBeingEdited }) =>
        $isBeingEdited &&
        `
      overflow-y: auto;

      .ProseMirror-selectednode[data-resize-container] img {
          outline: 2px solid rgba(104, 206, 248, 0.6);
          outline-offset: -2px;
          border-radius: 4px;
      }
    `}
`;

const ActionsFooterWrapper = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
`;

const StyledButton = styled(Button)`
    margin-left: 5px;
`;

const Tiptap: React.FC<TiptapProps> = ({ editor, textBlockId, content, dashboardId }) => {
    const { editorBeingEdited } = useTextBlockData();

    const { setEditorBeingEdited } = dispatchTextBlockData();
    const { updateTextBlock } = useTextBlock();
    const { execute: update, isLoading, data } = useCase(updateTextBlock, false);
    const { getLayouts } = dispatchReportLayoutsData();
    const [isUpdating, setIsUpdating] = useState(false);

    const confirmChanges = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (editor && textBlockId) {
            const htmlContent = editor.getHTML();
            update(UpdateTextBlockCriteriaBuilder.create().withRichText(htmlContent).withWidgetId(textBlockId).build());
            setIsUpdating(true);
        }
    };

    useEffect(() => {
        if (isSome(data)) {
            setIsUpdating(false);
            setEditorBeingEdited(null);
        }
    }, [data]);

    useEffect(() => {
        if (!isLoading && isUpdating && dashboardId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(dashboardId).build());
        }
    }, [isLoading, isUpdating, dashboardId]);

    const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (editor && content) {
            editor.commands.setContent(content);
        }
        setEditorBeingEdited(null);
    };

    const dashboardFrame = isDashboardFrame(textBlockId);

    return (
        editor && (
            <TiptapWrapper data-testid='tiptap-editor'>
                <EditorContentWrapper $isDashboardFrame={dashboardFrame}>
                    <StyledEditorContent
                        data-testid='editor-content'
                        $isBeingEdited={editorBeingEdited?.textBlockId === textBlockId}
                        editor={editor}
                    />
                </EditorContentWrapper>
                {editorBeingEdited?.textBlockId === textBlockId && (
                    <ActionsFooterWrapper>
                        <StyledButton
                            variant='primary'
                            onClick={(e) => confirmChanges(e)}
                            data-testid='tiptap-confirm-changes-button'
                            size='small'
                            text={i18n.t('common.save')}
                            disabled={isLoading && isUpdating}
                        />
                        <StyledButton
                            variant='outline'
                            onClick={handleCancel}
                            data-testid='tiptap-cancel-changes-button'
                            size='small'
                            text={i18n.t('common.cancel')}
                        />
                    </ActionsFooterWrapper>
                )}
            </TiptapWrapper>
        )
    );
};

export default Tiptap;
